import React, { useCallback, useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Bug, Lightbulb, Trash2, Plus, Megaphone, Video, FileText } from "lucide-react";
import { FEEDBACK_STATUS_META, FeedbackStatusChip, FeedbackDialog } from "@/components/FeedbackDialog";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Admin triage board for bugs + upgrade requests (Management portal
// "Issues" tab + the Desktop "Issue Tracker" app).
export default function FeedbackAdmin() {
  const [kind, setKind] = useState("bug");
  const [statusFilter, setStatusFilter] = useState("open");
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ bug: 0, upgrade: 0 });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [logsFor, setLogsFor] = useState(null);

  const watchVideo = async (f) => {
    setBusyId(f.id);
    try {
      const r = await api.get(`/feedback/video/${f.video_id}`, { responseType: "blob" });
      setVideoUrl(URL.createObjectURL(r.data));
    } catch { toast.error("Couldn't load the recording."); }
    finally { setBusyId(null); }
  };

  const load = useCallback(() => {
    api.get("/feedback/all")
      .then((r) => { setRows(r.data?.items || []); setCounts(r.data?.open_counts || {}); })
      .catch((e) => toast.error(formatErr(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const visible = rows.filter((f) => f.type === kind).filter((f) =>
    statusFilter === "all" ? true : statusFilter === "open" ? ["pending", "in_progress"].includes(f.status) : f.status === statusFilter);

  const setStatus = async (f, status) => {
    setBusyId(f.id);
    try {
      await api.patch(`/feedback/${f.id}`, { status });
      toast.success(`"${f.title}" → ${FEEDBACK_STATUS_META[status].label}.`);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusyId(null); }
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete "${f.title}"?`)) return;
    setBusyId(f.id);
    try { await api.delete(`/feedback/${f.id}`); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusyId(null); }
  };

  return (
    <div className="space-y-4 p-1" data-testid="feedback-admin">
      <div className="flex items-center justify-between gap-3 flex-wrap mobile-stack">
        <div className="flex gap-1 overflow-x-auto scrollbar-none mobile-full">
          {[
            { k: "bug", label: "Bugs", icon: Bug, n: counts.bug },
            { k: "upgrade", label: "Upgrade requests", icon: Lightbulb, n: counts.upgrade },
          ].map(({ k, label, icon: Icon, n }) => (
            <button
              key={k}
              onClick={() => setKind(k)}
              className={`flex items-center gap-2 px-3 py-2 min-h-11 text-sm font-semibold rounded-sm border transition-colors whitespace-nowrap flex-1 ${kind === k ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}
              data-testid={`feedback-kind-${k}`}
            >
              <Icon className="w-4 h-4" /> {label}
              {n > 0 && <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${kind === k ? "bg-primary-foreground text-primary" : "bg-rose-100 text-rose-700"}`}>{n}</span>}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap mobile-full">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-11 sm:h-9 w-full sm:w-40 rounded-sm" data-testid="feedback-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open (pending + in progress)</SelectItem>
              <SelectItem value="pending">Pending review</SelectItem>
              <SelectItem value="in_progress">In progress</SelectItem>
              <SelectItem value="fixed">Fixed</SelectItem>
              <SelectItem value="declined">Declined</SelectItem>
              <SelectItem value="all">Everything</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => setReportOpen(true)} className="rounded-sm flex-1 sm:flex-none" data-testid="feedback-new-report">
            <Plus className="w-4 h-4 mr-2" /> New report
          </Button>
        </div>
      </div>

      <div className="border border-border rounded-sm overflow-hidden bg-background">
        {loading && <div className="px-4 py-10 text-center text-sm text-muted-foreground">Loading…</div>}
        {!loading && visible.length === 0 && (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">
            Nothing here. {kind === "bug" ? "No bugs reported" : "No upgrade requests"} for this filter.
          </div>
        )}
        {visible.map((f) => (
          <div key={f.id} className="px-4 py-3 border-b border-border last:border-b-0" data-testid={`feedback-row-${f.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm flex items-center gap-2 flex-wrap">
                  {f.title}
                  <FeedbackStatusChip status={f.status} />
                </div>
                {f.details && <p className="text-sm text-muted-foreground mt-0.5 whitespace-pre-wrap">{f.details}</p>}
                {(f.images || []).length > 0 && (
                  <div className="flex gap-1.5 mt-1.5 flex-wrap">
                    {f.images.map((src, i) => (
                      <button key={i} type="button" onClick={() => setLightbox(f.images)} className="border border-border rounded-sm overflow-hidden hover:opacity-80" data-testid={`feedback-img-${f.id}-${i}`}>
                        <img src={src} alt={`screenshot ${i + 1}`} className="w-14 h-14 object-cover" />
                      </button>
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  {f.submitter_name || f.submitter_email} · <span className="uppercase tracking-wider">{f.portal}</span> portal · {new Date(f.created_at).toLocaleDateString()}
                </p>
                {(f.video_id || f.logs) && (
                  <div className="flex gap-2 mt-1.5">
                    {f.video_id && (
                      <button type="button" onClick={() => watchVideo(f)} disabled={busyId === f.id} className="inline-flex items-center gap-1 text-xs font-semibold text-sky-700 hover:underline" data-testid={`feedback-watch-${f.id}`}>
                        <Video className="w-3.5 h-3.5" /> Watch recording
                      </button>
                    )}
                    {f.logs && (
                      <button type="button" onClick={() => setLogsFor(f)} className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:underline" data-testid={`feedback-logs-${f.id}`}>
                        <FileText className="w-3.5 h-3.5" /> Technical log
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1 w-full sm:w-auto justify-end">
                <Select value={f.status} onValueChange={(v) => setStatus(f, v)} disabled={busyId === f.id}>
                  <SelectTrigger className="h-11 sm:h-8 w-full sm:w-36 rounded-sm text-xs" data-testid={`feedback-set-status-${f.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending review</SelectItem>
                    <SelectItem value="in_progress">In progress</SelectItem>
                    <SelectItem value="fixed">Fixed</SelectItem>
                    <SelectItem value="declined">Declined</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" variant="ghost" onClick={() => remove(f)} disabled={busyId === f.id} className="rounded-sm text-destructive" data-testid={`feedback-delete-${f.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <FeedbackDialog open={reportOpen} onOpenChange={(v) => { setReportOpen(v); if (!v) load(); }} client={api} base="/feedback" />
      {lightbox && <PhotoLightbox photos={lightbox} alt="screenshot" onClose={() => setLightbox(null)} />}

      <Dialog open={!!videoUrl} onOpenChange={(v) => { if (!v) { URL.revokeObjectURL(videoUrl); setVideoUrl(null); } }}>
        <DialogContent className="max-w-3xl" data-testid="feedback-video-dialog">
          <DialogHeader><DialogTitle>Screen recording</DialogTitle></DialogHeader>
          {videoUrl && <video src={videoUrl} controls autoPlay className="w-full rounded-sm bg-black" data-testid="feedback-video-player" />}
        </DialogContent>
      </Dialog>

      <Dialog open={!!logsFor} onOpenChange={(v) => { if (!v) setLogsFor(null); }}>
        <DialogContent className="max-w-2xl" data-testid="feedback-logs-dialog">
          <DialogHeader><DialogTitle>Technical log · {logsFor?.title}</DialogTitle></DialogHeader>
          <pre className="text-xs bg-secondary/40 border border-border rounded-sm p-3 max-h-96 overflow-auto whitespace-pre-wrap">{logsFor?.logs}</pre>
          <Button variant="outline" className="rounded-sm" onClick={() => { navigator.clipboard.writeText(logsFor?.logs || "").then(() => toast.success("Copied — paste it to the builder chat.")); }} data-testid="feedback-logs-copy">
            Copy log
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Submit-only view for signed-in staff who aren't full admins (they can
// report + track their own items, but not triage everyone's).
export function IssuesSubmitPanel() {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-border rounded-sm p-8 text-center space-y-3" data-testid="issues-submit-panel">
      <Megaphone className="w-8 h-8 mx-auto text-muted-foreground" />
      <p className="text-sm text-muted-foreground max-w-md mx-auto">
        Found a bug or have an idea for an upgrade? Send it to the office — you&apos;ll see the status of everything you reported.
      </p>
      <Button onClick={() => setOpen(true)} className="rounded-sm" data-testid="issues-submit-open">
        <Plus className="w-4 h-4 mr-2" /> Report an issue / suggest an upgrade
      </Button>
      <FeedbackDialog open={open} onOpenChange={setOpen} client={api} base="/feedback" />
    </div>
  );
}
